import { Box, Stack } from "@mui/material";
import Button from "@mui/material/Button";
import { ConstantContext } from "components/context/ConstantProvider";
import { Form, FormContext } from "components/shared/Form";
import { useMultipleQuery } from "components/shared/useMultipleQuery";
import DictionaryField from "components/ui/atoms/formFields/DictionaryField";
import ListSelectField from "components/ui/atoms/formFields/ListSelectField";
import RHFDatePicker from "components/ui/atoms/formFields/RHFDatePicker";
import RHFTextField from "components/ui/atoms/formFields/RHFTextField";
import TagField from "components/ui/atoms/formFields/TagField";
import UserField from "components/ui/atoms/formFields/UserField";
import { useContext } from "react";
import {
  useAllAddressAreas,
  useAllAddressCountries,
  useAllAddressLocalities,
} from "../address/apiAddress";

const asArray = (obj) => {
  return Array.isArray(obj) ? obj : obj ? [obj] : [];
};

const prepareInitialValues = (object) => ({
  sourcing_owner: asArray(object?.sourcing_owner),
  deal_flow: asArray(object?.deal_flow),
  status: asArray(object?.status),
  company__impact_model_tags: asArray(object?.company__impact_model_tags),
  company__sector_tags: asArray(object?.company__sector_tags),
  company__business_model_tags: asArray(object?.company__business_model_tags),
  company__mapping_and_certification_tags: asArray(
    object?.company__mapping_and_certification_tags,
  ),
  reason_to_close_tag: asArray(object?.reason_to_close_tag),
  company__headquarter_address__country: asArray(
    object?.company__headquarter_address__country,
  ),
  company__headquarter_address__administrative_area_level_1: asArray(
    object?.company__headquarter_address__administrative_area_level_1,
  ),
  company__headquarter_address__locality: asArray(
    object?.company__headquarter_address__locality,
  ),
  created_at__gte: object?.created_at__gte || null,
  created_at__lte: object?.created_at__lte || null,
  company__last_fundraising_amount__gte:
    object?.company__last_fundraising_amount__gte || "",
  company__last_fundraising_amount__lte:
    object?.company__last_fundraising_amount__lte || "",
  company__total_amount_raised__gte:
    object?.company__total_amount_raised__gte || "",
  company__total_amount_raised__lte:
    object?.company__total_amount_raised__lte || "",
});

const SourcingFilterFormFields = ({}) => {
  const { sourcingStatuses, dealFlows } = useContext(ConstantContext);

  const {
    data: { addressCountries, addressAreas, addressLocalities },
    isSuccess,
  } = useMultipleQuery({
    addressCountries: useAllAddressCountries(),
    addressAreas: useAllAddressAreas(),
    addressLocalities: useAllAddressLocalities(),
  });

  const { onSubmit } = useContext(FormContext);

  if (!isSuccess) return <>Loading</>;

  return (
    <Stack spacing={2}>
      <UserField name="sourcing_owner" label="Owner" multiple />
      <DictionaryField
        optionsDictionary={Object.fromEntries(
          dealFlows.map((x) => [x.id, x.name]),
        )}
        multiple
        name="deal_flow"
        label="Dealflow"
      />
      <ListSelectField
        options={sourcingStatuses.map((x) => [x.code, x.label])}
        multiple
        name="status"
        label="Status"
      />
      <DictionaryField
        optionsDictionary={Object.fromEntries(
          addressCountries.map((x) => [x.id, x.name]),
        )}
        multiple
        name="company__headquarter_address__country"
        label="Country"
      />
      <DictionaryField
        optionsDictionary={Object.fromEntries(
          addressAreas.map((x) => [
            x.administrative_area_level_1,
            x.administrative_area_level_1,
          ]),
        )}
        multiple
        name="company__headquarter_address__administrative_area_level_1"
        label="Area"
      />
      <DictionaryField
        optionsDictionary={Object.fromEntries(
          addressLocalities.map((x) => [x.locality, x.locality]),
        )}
        multiple
        name="company__headquarter_address__locality"
        label="Locality"
      />
      <TagField
        tagTypeId="TAG_SECTOR"
        multiple
        name="company__sector_tags"
        label="Sector tags"
      />
      <TagField
        tagTypeId="TAG_IMPACT_MODEL"
        multiple
        name="company__impact_model_tags"
        label="Impact model tags"
      />
      <TagField
        tagTypeId="TAG_BUSINESS_MODEL"
        multiple
        name="company__business_model_tags"
        label="Business model tags"
      />
      <TagField
        tagTypeId="TAG_MAPPING_AND_CERTIFICATIONS"
        multiple
        name="company__mapping_and_certification_tags"
        label="Mapping and Certifications tags"
      />
      <TagField
        tagTypeId="TAG_REASON_TO_CLOSE"
        multiple
        name="reason_to_close_tag"
        label="Reason to close tags"
      />
      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <RHFDatePicker
          type="number"
          name="created_at__gte"
          label="Min creation date"
          placeholder="min"
        />
        <RHFDatePicker
          sx={{ ml: 2 }}
          type="number"
          name="created_at__lte"
          label="Max creation date"
          placeholder="max"
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <RHFTextField
          type="number"
          name="company__last_fundraising_amount__gte"
          label="Min last fundraising amount"
          placeholder="min"
        />
        <RHFTextField
          sx={{ ml: 2 }}
          type="number"
          name="company__last_fundraising_amount__lte"
          label="Max last fundraising amount"
          placeholder="max"
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <RHFTextField
          type="number"
          name="company__total_amount_raised__gte"
          label="Min total amount raised"
          placeholder="min"
        />
        <RHFTextField
          sx={{ ml: 2 }}
          type="number"
          name="company__total_amount_raised__lte"
          label="Max total amount raised"
          placeholder="max"
        />
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" onClick={onSubmit}>
          Apply
        </Button>
      </Box>
    </Stack>
  );
};

const SourcingFilterForm = (props) => {
  return (
    <Form prepareInitialValues={prepareInitialValues} {...props}>
      <SourcingFilterFormFields />
    </Form>
  );
};

export default SourcingFilterForm;
